import { useEffect, useState, useRef } from "react";
import "./RetailPos.css";
import ContactForm from "../../components/ContactUs/ContactUs";
import PosDesk from "../../assets/images/pos_desk.png";
import {
  ShoppingCart,
  Smartphone,
  CreditCard,
  Printer,
  Users,
  Building2,
  CheckCircle,
  Monitor,
  Wifi,
  BarChart3,
  Shield,
} from "lucide-react";

const RetailPOS = () => {
  // Inline scroll to top functionality
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [showContact, setShowContact] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());

  // Refs for scroll animations
  const featuresRef = useRef(null);
  const industriesRef = useRef(null);
  const benefitsRef = useRef(null);
  const ctaRef = useRef(null);

  // Trigger hero animations
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute("data-section");
          if (sectionName) {
            setVisibleSections((prev) => new Set([...prev, sectionName]));
          }
        }
      });
    }, observerOptions);

    const refs = [featuresRef, industriesRef, benefitsRef, ctaRef];
    refs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  const industries = [
    {
      icon: Monitor,
      title: "Consumer Electronics",
      description:
        "Complete POS solutions for electronics retailers with inventory management and warranty tracking.",
    },
    {
      icon: Users,
      title: "Fashion and Apparel",
      description:
        "Specialized retail management for clothing stores with size, color, and seasonal inventory control.",
    },
    {
      icon: ShoppingCart,
      title: "Beauty and Fragrance",
      description:
        "Tailored POS systems for beauty retailers with customer loyalty and product recommendation features.",
    },
    {
      icon: Building2,
      title: "Restaurants and Foodservice",
      description:
        "Comprehensive restaurant POS with table management, kitchen integration, and order tracking.",
    },
    {
      icon: CreditCard,
      title: "Specialty Retail / POS",
      description:
        "Flexible POS solutions for specialized retail environments with custom workflow support.",
    },
    {
      icon: Smartphone,
      title: "Furniture and Decor",
      description:
        "Advanced retail management for furniture stores with delivery scheduling and custom orders.",
    },
  ];

  const benefits = [
    "Improve customer experience with faster checkout and personalized service",
    "Cloud POS Systems allow retailers to take their businesses anywhere",
    "Ensure the security of customer data with advanced encryption and compliance",
    "Provide complete and precise sales reports with real-time analytics",
    "Speed up product return processes with integrated return management",
    "Real-time inventory synchronization across all locations",
    "Integrated loyalty programs and customer relationship management",
    "Multi-payment method support including contactless and mobile payments",
    "Automated tax calculations and compliance reporting",
    "Seamless integration with accounting and ERP systems",
  ];

  const posFeatures = [
    {
      icon: Printer,
      title: "Multi-Printer Support",
      description:
        "Connect to fiscal printers, thermal printers, barcode printers, and A4 format printers.",
    },
    {
      icon: Wifi,
      title: "Online/Offline Operation",
      description:
        "Works seamlessly both online with server connection and offline for uninterrupted service.",
    },
    {
      icon: BarChart3,
      title: "Real-time Synchronization",
      description:
        "Instant updates of prices, inventory, and customer data with one-click server sync.",
    },
    {
      icon: Shield,
      title: "User Activity Logging",
      description:
        "Complete audit trail of all user movements and transactions for security and compliance.",
    },
  ];

  return (
    <div className="retail-pos-main-container">
      {/* Hero Section */}
      <section className="retail-pos-hero-wrapper">
        <div className="retail-pos-content-width">
          <div className="retail-pos-center-align">
            <h1
              className={`retail-pos-hero-main-title ${
                isVisible ? "retail-pos-slide-up" : ""
              }`}
            >
              <span className="retail-pos-gradient-text">RETAIL & POS</span>
            </h1>
            <p
              className={`retail-pos-hero-subtitle ${
                isVisible ? "retail-pos-fade-in" : ""
              }`}
            >
              The point of sale (POS) or point of purchase (POP) is the time and
              place where a retail transaction is completed. At the point of
              sale, the merchant calculates the amount owed by the customer,
              indicates that amount, may prepare an invoice for the customer,
              and indicates the options for the customer to make payment.
            </p>
            <div
              className={`retail-pos-hero-buttons ${
                isVisible ? "retail-pos-bounce-in" : ""
              }`}
            >
              <button
                className="retail-pos-primary-button"
                onClick={() => (window.location.href = "/products/portfolio")}
              >
                <ShoppingCart className="retail-pos-small-icon" />
                Explore POS Solutions
              </button>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="retail-pos-hero-background">
          <div className="retail-pos-circle-decoration-one"></div>
          <div className="retail-pos-circle-decoration-two"></div>
          <div className="retail-pos-floating-element retail-pos-element-1"></div>
          <div className="retail-pos-floating-element retail-pos-element-2"></div>
          <div className="retail-pos-floating-element retail-pos-element-3"></div>
        </div>
      </section>

      {/* POS Overview Section */}
      <section className="retail-pos-overview-area">
        <div className="retail-pos-content-width">
          <div className="retail-pos-overview-content">
            <p className="retail-pos-overview-description">
              It is also the point at which a customer makes a payment to the
              merchant in exchange for goods or after provision of a service.
              After receiving payment, the merchant may issue a receipt for the
              transaction, which is usually printed but can also be dispensed
              with or sent electronically.
            </p>
          </div>
        </div>
      </section>

      {/* POS Retail Section with Image */}
      <section className="retail-pos-main-section">
        <div className="retail-pos-content-width">
          <div className="retail-pos-main-layout">
            <div className="retail-pos-main-content">
              <h2 className="retail-pos-main-heading">POS Retail</h2>
              <p className="retail-pos-main-description">
                Point Of Sales (POS) – Every item that is purchased must be
                output both logically and in accounting, Dynamics NAV offers and
                supports all types of POS software, importing items, selling
                prices, printing prices, Consumer, Vendor, Users and all the
                information we need for a real-time POS and possibly any changes
                we make to sending it directly to POS.
              </p>
              <p className="retail-pos-main-description">
                With POS we mean selling all retail items offers many Dynamic
                Opportunities such as that is, connect to any Fiscal Printer,
                Thermal Printer, Barcode and Printer Printer, Retail Return,
                Cancel a Paragraph, and save all User Movements that are logged
                in to POS.
              </p>
              <p className="retail-pos-main-description">
                When working in POS if we have sales price changes or any item
                in no time wasted without logging out of the system we have the
                option with a CLICK to import all changes from the Main Server.
                POS works offline and online, offline works locally while online
                has direct connection to server for receiving changes.
              </p>
            </div>
            <div className="retail-pos-main-image">
              <img
                src={PosDesk || "/placeholder.svg"}
                alt="Modern POS retail system interface showing product catalog, payment processing, and receipt printing"
                className="retail-pos-showcase-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* POS Features Section */}
      <section
        className="retail-pos-features-area"
        ref={featuresRef}
        data-section="features"
      >
        <div className="retail-pos-content-width">
          <div className="retail-pos-center-align">
            <h2
              className={`retail-pos-features-heading ${
                visibleSections.has("features") ? "retail-pos-slide-up" : ""
              }`}
            >
              POS System Features
            </h2>
            <p
              className={`retail-pos-features-description ${
                visibleSections.has("features") ? "retail-pos-fade-in" : ""
              }`}
            >
              Comprehensive POS functionality designed for modern retail
              environments with maximum flexibility and reliability.
            </p>
          </div>

          <div className="retail-pos-features-layout">
            {posFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`retail-pos-feature-item ${
                    visibleSections.has("features") ? "retail-pos-slide-up" : ""
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="retail-pos-feature-item-top">
                    <div className="retail-pos-feature-item-icon">
                      <Icon className="retail-pos-medium-icon" />
                    </div>
                    <h3 className="retail-pos-feature-item-title">
                      {feature.title}
                    </h3>
                  </div>
                  <div className="retail-pos-feature-item-body">
                    <p className="retail-pos-feature-item-text">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mobile POS Section */}
      <section className="retail-pos-mobile-area">
        <div className="retail-pos-content-width">
          <div className="retail-pos-mobile-content">
            <span className="retail-pos-mobile-badge">Mobile POS</span>
            <h2 className="retail-pos-mobile-heading">POS Retail - Mobile</h2>
            <p className="retail-pos-mobile-description">
              We can also make POS sales using the Mobile application, where the
              application can be connected to any printer whether fiscal,
              thermal, or A4 format. In real time on the device we have stock,
              sales prices, customers, locals, routes and all we need for
              retail.
            </p>
            <p className="retail-pos-mobile-description">
              The app works both offline and online like the Mobile app,
              ensuring continuous operation regardless of network connectivity.
            </p>
            <div className="retail-pos-mobile-highlights">
              <div className="retail-pos-mobile-highlight">
                <CheckCircle className="retail-pos-check-icon" />
                <span>Real-time inventory access</span>
              </div>
              <div className="retail-pos-mobile-highlight">
                <CheckCircle className="retail-pos-check-icon" />
                <span>Multi-printer connectivity</span>
              </div>
              <div className="retail-pos-mobile-highlight">
                <CheckCircle className="retail-pos-check-icon" />
                <span>Offline/online operation</span>
              </div>
              <div className="retail-pos-mobile-highlight">
                <CheckCircle className="retail-pos-check-icon" />
                <span>Customer and route management</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section
        className="retail-pos-industries-area"
        ref={industriesRef}
        data-section="industries"
      >
        <div className="retail-pos-content-width">
          <div className="retail-pos-center-align">
            <h2
              className={`retail-pos-industries-heading ${
                visibleSections.has("industries") ? "retail-pos-slide-up" : ""
              }`}
            >
              We can help you to manage these industries:
            </h2>
          </div>

          <div className="retail-pos-industries-layout">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={index}
                  className={`retail-pos-industry-item ${
                    visibleSections.has("industries")
                      ? "retail-pos-slide-up"
                      : ""
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="retail-pos-industry-item-top">
                    <div className="retail-pos-industry-item-icon">
                      <Icon className="retail-pos-medium-icon" />
                    </div>
                    <h3 className="retail-pos-industry-item-title">
                      {industry.title}
                    </h3>
                  </div>
                  <div className="retail-pos-industry-item-body">
                    <p className="retail-pos-industry-item-text">
                      {industry.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        className="retail-pos-benefits-area"
        ref={benefitsRef}
        data-section="benefits"
      >
        <div className="retail-pos-content-width">
          <div className="retail-pos-center-align">
            <h2
              className={`retail-pos-benefits-heading ${
                visibleSections.has("benefits") ? "retail-pos-slide-up" : ""
              }`}
            >
              Benefits
            </h2>
            <p
              className={`retail-pos-benefits-description ${
                visibleSections.has("benefits") ? "retail-pos-fade-in" : ""
              }`}
            >
              Transform your retail operations with comprehensive benefits and
              proven results.
            </p>
          </div>

          <div className="retail-pos-benefits-layout">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`retail-pos-benefit-item ${
                  visibleSections.has("benefits") ? "retail-pos-slide-up" : ""
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CheckCircle className="retail-pos-benefit-check-icon" />
                <span className="retail-pos-benefit-text">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="retail-pos-cta-area" ref={ctaRef} data-section="cta">
        <div className="retail-pos-content-width retail-pos-center-align">
          <h2
            className={`retail-pos-cta-heading ${
              visibleSections.has("cta") ? "retail-pos-zoom-in" : ""
            }`}
          >
            Ready to Transform Your Retail Business?
          </h2>
          <p
            className={`retail-pos-cta-description ${
              visibleSections.has("cta") ? "retail-pos-fade-in" : ""
            }`}
          >
            Let our retail experts design a comprehensive POS solution that
            streamlines your operations and enhances customer experience with
            Microsoft Dynamics NAV.
          </p>
          <div
            className={`retail-pos-cta-buttons ${
              visibleSections.has("cta") ? "retail-pos-bounce-in" : ""
            }`}
          >
            <button
              className="retail-pos-primary-button"
              onClick={() => setShowContact(true)}
            >
              <Users className="retail-pos-small-icon" />
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContact && (
        <div
          className="modal-overlay retail-pos-modal-fade-in"
          onClick={() => setShowContact(false)}
        >
          <div
            className="modal-content retail-pos-modal-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowContact(false)}
            >
              ✕
            </button>
            <ContactForm />
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailPOS;
